/**
 * Customer Accounts Redux Sagas
 */

import { call, put, select, takeLatest } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { customerAccountsApi } from '../infrastructure/api/customer-accounts-api'
import { customerAccountsActions } from './customer-accounts.slice'
import type {
  CustomerAccount,
  CustomerAccountsFilter,
  CustomerAccountsState,
  PaginatedCustomerAccounts,
  UpdateAccountPayload,
  ChangeAccountStatusPayload,
  UpdateAllowedCitiesPayload,
} from '../domain/types'

interface RootState {
  customerAccounts: CustomerAccountsState
}

function* selectCurrentFilter(): Generator<unknown, CustomerAccountsFilter, unknown> {
  const filter: CustomerAccountsFilter = (yield select(
    (state: RootState) => state.customerAccounts.filter
  )) as CustomerAccountsFilter
  return filter ?? {}
}

function* fetchAccountsSaga(action: PayloadAction<CustomerAccountsFilter | undefined>) {
  try {
    const response: PaginatedCustomerAccounts = yield call(customerAccountsApi.getAccounts, action.payload)
    yield put(customerAccountsActions.fetchAccountsSuccess(response))
  } catch (error) {
    yield put(customerAccountsActions.fetchAccountsFailure((error as Error).message))
  }
}

function* updateAccountSaga(action: PayloadAction<UpdateAccountPayload>) {
  try {
    const account: CustomerAccount = yield call(
      customerAccountsApi.updateAccount,
      action.payload.accountId,
      action.payload.data
    )
    yield put(customerAccountsActions.updateAccountSuccess(account))
    const filter = yield* selectCurrentFilter()
    yield put(customerAccountsActions.fetchAccounts(filter))
  } catch (error) {
    yield put(customerAccountsActions.updateAccountFailure((error as Error).message))
  }
}

function* changeAccountStatusSaga(action: PayloadAction<ChangeAccountStatusPayload>) {
  try {
    const account: CustomerAccount = yield call(
      customerAccountsApi.changeStatus,
      action.payload.accountId,
      action.payload.status
    )
    yield put(customerAccountsActions.changeAccountStatusSuccess(account))
    const filter = yield* selectCurrentFilter()
    yield put(customerAccountsActions.fetchAccounts(filter))
  } catch (error) {
    yield put(customerAccountsActions.changeAccountStatusFailure((error as Error).message))
  }
}

function* updateAllowedCitiesSaga(action: PayloadAction<UpdateAllowedCitiesPayload>) {
  try {
    const account: CustomerAccount = yield call(
      customerAccountsApi.updateAllowedCities,
      action.payload.accountId,
      action.payload.cityIds
    )
    yield put(customerAccountsActions.updateAllowedCitiesSuccess(account))
    const filter = yield* selectCurrentFilter()
    yield put(customerAccountsActions.fetchAccounts(filter))
  } catch (error) {
    yield put(customerAccountsActions.updateAllowedCitiesFailure((error as Error).message))
  }
}

export function* customerAccountsSaga() {
  yield takeLatest(customerAccountsActions.fetchAccounts.type, fetchAccountsSaga)
  yield takeLatest(customerAccountsActions.updateAccount.type, updateAccountSaga)
  yield takeLatest(customerAccountsActions.changeAccountStatus.type, changeAccountStatusSaga)
  yield takeLatest(customerAccountsActions.updateAllowedCities.type, updateAllowedCitiesSaga)
}
